import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { inspectionService } from '@/services/inspection.service';
import { useHasPermission } from '@/hooks/use-permissions';
import { SearchBar } from '@/components/shared/SearchBar';
import { Pagination } from '@/components/shared/Pagination';
import { EmptyState } from '@/components/shared/EmptyState';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { ROUTES } from '@/routes/routes';

const PAGE_SIZE = 20;

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

export function JobPoolPage() {
  const can = useHasPermission();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['job-pool', page, debouncedSearch],
    queryFn: () =>
      inspectionService.getJobPool({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
      }),
  });

  const claimMutation = useMutation({
    mutationFn: (id: string) => inspectionService.claim(id),
    onSuccess: (_, id) => {
      toast.success('Inspection claimed — it is now in your queue.');
      void qc.invalidateQueries({ queryKey: ['job-pool'] });
      navigate(ROUTES.INSPECTION_DETAIL.replace(':id', id));
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to claim inspection')),
  });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Job Pool</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Unassigned inspections available to claim.
        </p>
      </div>

      <SearchBar
        value={search}
        onChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        placeholder="Search by inspection number, equipment, or customer…"
      />

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
          Loading job pool…
        </div>
      ) : data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[700px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Inspection #</th>
                  <th className="px-4 py-3 font-medium text-foreground">Equipment</th>
                  <th className="px-4 py-3 font-medium text-foreground">Customer</th>
                  <th className="px-4 py-3 font-medium text-foreground">Service Type</th>
                  <th className="px-4 py-3 font-medium text-foreground">Priority</th>
                  {can('inspection:claim') && (
                    <th className="px-4 py-3 font-medium text-foreground sr-only">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.data.map((insp) => (
                  <tr
                    key={insp.id}
                    className="border-b border-border last:border-0 hover:bg-muted/20"
                  >
                    <td
                      className="px-4 py-3 font-mono text-xs font-medium text-primary hover:underline cursor-pointer"
                      onClick={() => navigate(ROUTES.INSPECTION_DETAIL.replace(':id', insp.id))}
                    >
                      {insp.inspectionNumber}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{insp.equipmentName}</div>
                      <div className="text-xs text-muted-foreground">
                        {insp.equipmentAssetNumber}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {insp.customerOrganizationName ?? insp.customerName ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {insp.serviceTypeName ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{insp.priorityName ?? '—'}</td>
                    {can('inspection:claim') && (
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          onClick={() => claimMutation.mutate(insp.id)}
                          disabled={claimMutation.isPending}
                        >
                          {claimMutation.isPending && claimMutation.variables === insp.id ? (
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                          ) : (
                            'Claim'
                          )}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : isError ? (
        <QueryErrorState subject="the job pool" onRetry={() => void refetch()} />
      ) : (
        <EmptyState
          icon={faBoxOpen}
          title={search ? 'No jobs match your search' : 'Job pool is empty'}
          description={
            search
              ? 'Try a different search term.'
              : 'No unassigned inspections are currently available.'
          }
        />
      )}
    </div>
  );
}
